import { PASSWORD_REJECTED_TEXT } from './passwordPolicy';
import type { Role } from './auth';
// Vertaalt Cognito- en API-fouten naar Nederlandse zinnen voor de UI.

const COGNITO_MESSAGES: Record<string, string> = {
  NotAuthorizedException: 'Onjuist e-mailadres of wachtwoord.',
  UserNotFoundException: 'Geen account gevonden met dit e-mailadres.',
  UserNotConfirmedException: 'Dit account is nog niet bevestigd. Controleer je e-mail.',
  PasswordResetRequiredException: 'Stel eerst een nieuw wachtwoord in.',
  CodeMismatchException: 'De verificatiecode klopt niet.',
  ExpiredCodeException: 'De verificatiecode is verlopen. Vraag een nieuwe aan.',
  InvalidPasswordException: PASSWORD_REJECTED_TEXT,
  LimitExceededException: 'Te veel pogingen. Wacht een paar minuten en probeer het opnieuw.',
  TooManyRequestsException: 'Te veel verzoeken. Wacht even en probeer het opnieuw.',
  TooManyFailedAttemptsException: 'Te veel mislukte pogingen. Probeer het later opnieuw.',
  InvalidParameterException: 'Een van de ingevulde gegevens klopt niet.',
  UsernameExistsException: 'Er bestaat al een account met dit e-mailadres.',
  AliasExistsException: 'Dit e-mailadres is al in gebruik.',
  ResourceNotFoundException: 'Onderdeel niet gevonden. Ververs de pagina en probeer het opnieuw.',
};

const HTTP_MESSAGES: Record<number, string> = {
  400: 'De aanvraag klopt niet. Controleer de ingevulde gegevens en probeer het opnieuw.',
  401: 'Je sessie is verlopen. Log opnieuw in.',
  403: 'Je hebt geen toegang tot dit onderdeel. Vraag de beheerder van jouw organisatie om toegang.',
  404: 'Dit onderdeel bestaat niet of is verwijderd. Ga terug naar het overzicht.',
  408: 'De server reageerde niet op tijd.',
  409: 'Dit kan nu niet. Ververs de pagina; mogelijk heeft iemand anders dit al gewijzigd.',
  413: 'Het verzoek is te groot. Probeer het met minder gegevens tegelijk.',
  429: 'Te veel verzoeken. Wacht even en probeer het opnieuw.',
  503: 'Het is even druk. Probeer het zo opnieuw.',
};

const NETWORK_MESSAGE = 'Geen verbinding. Controleer je internet en probeer het opnieuw.';
const SERVER_MESSAGE = 'Er ging iets mis aan onze kant. Probeer het opnieuw of neem contact op.';
const FALLBACK_MESSAGE = 'Er ging iets mis. Probeer het opnieuw.';

/**
 * `role` bepaalt of serverdetail zichtbaar mag zijn. De backoffice mag het zien,
 * want dat is wat een storing diagnoseerbaar maakt zonder CloudWatch. Een klant
 * nooit: daar zit AWS-detail in. Laat je hem weg, dan geldt het strengste geval.
 */
export function translateError(err: unknown, role?: Role): string {
  if (err == null) {
    return FALLBACK_MESSAGE;
  }
  if (typeof err === 'string') {
    return err;
  }
  if (!(err instanceof Error)) {
    return FALLBACK_MESSAGE;
  }
  const cognito = COGNITO_MESSAGES[err.name];
  if (cognito) {
    return cognito;
  }
  const message = err.message ?? '';
  const httpMatch = /^(\d{3}):\s*(.*)$/.exec(message);
  if (httpMatch) {
    const status = Number(httpMatch[1]);
    const tail = httpMatch[2].trim();
    // Serverfouten niet doortonen aan een klant: daar zit infrastructuur-detail
    // in (AWS-excepties, stacktraces, interne paden). De backoffice krijgt ze
    // wel, inclusief de exception-naam, dus zonder de looksEnglish-check.
    if (status >= 500) {
      if (role === 'backoffice' && tail) {
        return tail;
      }
      return HTTP_MESSAGES[status] ?? SERVER_MESSAGE;
    }
    // Backend stuurt al NL-foutmeldingen (UTF-8, geen JSON-Engels). Gebruik die als ze er zijn.
    if (tail && !looksEnglish(tail)) {
      return tail;
    }
    if (HTTP_MESSAGES[status]) {
      return HTTP_MESSAGES[status];
    }
    return tail || FALLBACK_MESSAGE;
  }
  if (/Failed to fetch|NetworkError|Network request failed|AbortError/i.test(err.name + ' ' + message)) {
    return NETWORK_MESSAGE;
  }
  // Onze eigen NL-throws (bv. 'Cognito niet geconfigureerd') zijn al leesbaar.
  if (message && !looksEnglish(message)) {
    return message;
  }
  return FALLBACK_MESSAGE;
}

function looksEnglish(text: string): boolean {
  // Heuristiek: typisch Cognito/AWS-Engels, plus technische markers die sowieso
  // niet in een klantmelding thuishoren (exception-namen, doc-links, AWS-jargon).
  if (/https?:\/\/|\b\w+Exception\b/i.test(text)) {
    return true;
  }
  return /\b(Incorrect|Invalid|expired|exceed(s|ed)?|throughput|capacity|throttl\w*|DynamoDB|attempt|password|user(name)?|not (authorized|found|confirmed))\b/i.test(
    text,
  );
}
